import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useContext } from 'react'
import { AuthContext } from '../../contexts/TokenContext'
import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

const mapToMinimalsContact = (note) => {
  const { user } = note.person

  return {
    id: user.id,
    // avatar: '/static/mock-images/avatars/avatar_15.jpg',
    name: `${user.first_name} ${user.last_name}`,
    username: user.username,
  }
}
const displayName = (user) => `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username

const mapToMinimalsMessage = (note, isPrivate = false) => {
  const { user } = note.person
  return {
    id: note.id,
    body: note.text,
    contentType: 'text',
    attachments: [],
    createdAt: new Date(note.created),
    senderId: user.id,
    // `populateSelfMessages` has already rewritten your own id to 'me'.
    authorName: user.id === 'me' ? 'You' : displayName(user),
    // Editing is organisation-wide, so `senderId` (the author) does not tell a
    // reader whether the text in front of them is still what that person wrote.
    // Carry the editor so the thread can say so.
    isEdited: Boolean(note.is_edited),
    editedByName: note.edited_by ? displayName(note.edited_by.user) : null,
    // Which endpoint owns this note. The two tabs are backed by two different
    // tables, and a note can only be edited through its own one.
    isPrivate,
  }
}
const mapToMinimalsConversation = (variantId, notes, specificNotes) => {
  const id = variantId
  const participants = notes.map(mapToMinimalsContact)
  const type = 'GROUP'
  const unreadCount = 0
  const messages = notes.map((n) => mapToMinimalsMessage(n, false))
  const specificMessages = specificNotes.map((n) => mapToMinimalsMessage(n, true))

  return { id, participants, type, unreadCount, messages, specificMessages }
}
const populateSelfMessages = (userId, notes) => {
  return notes.map((note) => ({
    ...note,
    person: {
      ...note.person,
      user: {
        ...note.person.user,
        id: userId == note.person.user.id ? 'me' : note.person.user.id,
      },
    },
  }))
}
// `variantPath` is `${chrom}/${pos}/${ref}/${alt}` — the backend derives the allele-precise
// variant_match_id from it (and dual-looks-up the legacy chrom-pos id for old notes).
// `fileId` (the VcfFile of the table) puts the notes in that file's organisation, so
// a parent organisation's user on a sub-organisation's analysis reads and writes the
// sub-organisation's notes. Without it the notes are the user's own organisation's.
const notesQuery = (fileId) => (fileId ? `?file_id=${encodeURIComponent(fileId)}` : '')

const fetchVariantNotes = async (userId, variantPath, sampleName, fileId) => {
  if (!variantPath) {
    return undefined
  }
  const q = notesQuery(fileId)
  const { data } = await axios.get(`${API_BASE_URL}/variant/${variantPath}/notes${q}`)
  const { data: specificData } = await axios.get(`${API_BASE_URL}/variant/${variantPath}/${sampleName}/notes${q}`)
  const notes = populateSelfMessages(userId, data)
  const specificNotes = populateSelfMessages(userId, specificData)
  const res = mapToMinimalsConversation(variantPath, notes, specificNotes)
  return res
}
const insertVariantNote = async (variantPath, { note, isPrivate }, sampleName = '', fileId = null) => {
  const q = notesQuery(fileId)
  const { data } = !isPrivate
    ? await axios.post(`${API_BASE_URL}/variant/${variantPath}/notes${q}`, {
        text: note,
      })
    : await axios.post(`${API_BASE_URL}/variant/${variantPath}/${sampleName}/notes${q}`, {
        text: note,
      })

  return data
}
// Edit/withdraw are keyed by the note's OWN id, not the variant, and the two
// tabs are two different tables — so the route depends on which list the note
// came from. `isPrivate` is carried on the message for exactly this.
const noteDetailUrl = (noteId, isPrivate) => `${API_BASE_URL}/variant/${isPrivate ? 'sample-notes' : 'notes'}/${noteId}`

const updateVariantNote = async ({ noteId, text, isPrivate }) => {
  const { data } = await axios.patch(noteDetailUrl(noteId, isPrivate), { text })
  return data
}

const deleteVariantNote = async ({ noteId, isPrivate }) => {
  await axios.delete(noteDetailUrl(noteId, isPrivate))
  return noteId
}

export const useVariantNotes = ({ chrom, pos, ref, alt, sampleName, fileId = null }) => {
  const queryClient = useQueryClient()
  const { user } = useContext(AuthContext)

  const variantPath =
    chrom != null && pos != null && ref != null && alt != null ? `${chrom}/${pos}/${ref}/${alt}` : null

  const queryKey = ['variant-notes', user.id, variantPath, sampleName, fileId]
  const query = useQuery(queryKey, () => fetchVariantNotes(user.id, variantPath, sampleName, fileId))
  const mutation = useMutation(
    ({ note, isPrivate }) => insertVariantNote(variantPath, { note, isPrivate }, sampleName, fileId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey)
        queryClient.invalidateQueries(['variants'])
      },
    },
  )

  // Same invalidation as insert: the thread refetches, and `variants` refetches
  // because withdrawing the last note on a variant clears its has_note icon.
  const invalidate = {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
      queryClient.invalidateQueries(['variants'])
    },
  }
  const updateMutation = useMutation(updateVariantNote, invalidate)
  const deleteMutation = useMutation(deleteVariantNote, invalidate)

  return { query, mutation, updateMutation, deleteMutation }
}
